import { useEffect } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import {
  addNativeNotificationOpenedListener,
  consumePendingNativeNotification,
  getNativeNotificationStatus,
  nativeNotificationsAvailable,
  registerNativeNotifications,
  requestNativeNotificationPermission,
} from '../native/nativeNotifications';

function isLineMedic(profile) {
  return String(profile?.member_kind || 'line_medic') === 'line_medic';
}

async function authorizedHeaders() {
  const token = await getMemberAccessToken();
  if (!token) throw new Error('Member sign in required.');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function saveRegistration(registration) {
  const token = String(registration?.token || '').trim();
  if (!token) return;
  const response = await fetch('/api/notifications', {
    method: 'POST',
    headers: await authorizedHeaders(),
    body: JSON.stringify({
      action: 'register',
      token,
      permission: registration?.permission,
      environment: registration?.environment,
      bundleId: registration?.bundleId,
      appVersion: registration?.appVersion,
      appBuild: registration?.appBuild,
    }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload?.error || 'Unable to register this iPhone for notifications.');
  }
}

export default function NotificationRuntime({ profile }) {
  const lineMedic = isLineMedic(profile);

  useEffect(() => {
    if (!nativeNotificationsAvailable() || !lineMedic || !profile?.approved || profile?.disabled) return undefined;
    let cancelled = false;
    let listener = null;

    const routeNotificationPath = path => {
      const target = String(path || '').trim();
      if (!target.startsWith('/')) return;
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (current !== target) window.history.pushState({}, '', target);
      window.dispatchEvent(new CustomEvent('smfd:native-route', { detail: { path: target } }));
    };

    const consumePending = async () => {
      try {
        const pending = await consumePendingNativeNotification();
        if (!cancelled) routeNotificationPath(pending?.path);
      } catch (error) {
        console.warn('[Line Medic notifications] pending notification route failed', error?.message || error);
      }
    };

    addNativeNotificationOpenedListener(path => {
      routeNotificationPath(path);
      consumePendingNativeNotification().catch(() => {});
    }).then(next => {
      if (cancelled) next?.remove?.();
      else {
        listener = next;
        consumePending();
      }
    });

    window.addEventListener('smfd:native-resume', consumePending);

    getNativeNotificationStatus()
      .then(async status => {
        if (cancelled) return null;
        let current = status || {};
        const permission = String(current?.permission || '').toLowerCase();

        // 1.4 onboarding: on the first approved Line Medic launch, ask iOS for
        // notification permission while the system prompt is still available.
        // Existing enabled users simply refresh their APNs registration, while
        // users who previously denied permission are left alone and can recover
        // from Settings.
        if (permission === 'unknown') {
          current = await requestNativeNotificationPermission();
        }

        if (cancelled || !['enabled', 'provisional'].includes(String(current?.permission || '').toLowerCase())) {
          return null;
        }
        return registerNativeNotifications();
      })
      .then(registration => {
        if (cancelled || !registration?.token) return null;
        return saveRegistration(registration);
      })
      .catch(error => {
        console.warn('[Line Medic notifications] registration/onboarding failed', error?.message || error);
      });

    return () => {
      cancelled = true;
      window.removeEventListener('smfd:native-resume', consumePending);
      listener?.remove?.();
    };
  }, [profile?.id, profile?.approved, profile?.disabled, lineMedic]);

  return null;
}
